/**
 * lookRuntime — what the face graph's Look node does each frame.
 *
 * The Look node is one system: it decides where the character looks (the
 * Attention logic, attentionBrain.js) and drives the head, eyes and blinks to match. Under the hood it reuses the existing runtimes rather
 * than duplicating them:
 *
 *   attentionBrain   → where to look / which state            (decisions)
 *   lookTargetRuntime ← the shared look target it writes       (gate, directions, head speed)
 *   headFollowRuntime ← its Head settings + __headfollow__ weight (neck/head aim, tilt)
 *   eyeFollowRuntime  ← its Eyes settings + __eyefollow__ weight  (eye aim)
 *   blinkRuntime      ← its Blinks settings + __blink__ weight     (random + event blinks)
 *
 * The weights are returned to GraphEvaluator, which emits them as the same
 * sentinels the standalone Head Follow / Eye Follow / Blink nodes use — so the
 * renderers need no Look-specific path. The node is reached through Final
 * Pose's dedicated `look` input, not through Blend nodes: it is a set of
 * adjustments applied after the pose, not an animation to mix.
 *
 * One live Look node per face graph (last writer wins), like the nodes it
 * replaces. `step` must run at most once per frame.
 */

import { createAttention, stepAttention, attentionStateLabel } from './attentionBrain.js'
import { LOOK_DEFAULTS } from './lookNodeSettings.js'
import { lookTargetRuntime } from './lookTargetRuntime.js'
import { headFollowRuntime } from './headFollowRuntime.js'
import { eyeFollowRuntime } from './eyeFollowRuntime.js'
import { setConfig as setBlinkConfig, blinkNow } from './blinkRuntime.js'
import { debugState } from './debugState.js'

const FIELDS = Object.keys(LOOK_DEFAULTS)
const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now())

// Reused every frame — no per-frame allocation.
const cfg = { ...LOOK_DEFAULTS }
const st = createAttention()
const hfCfg = { maxYaw: 0, maxPitch: 0, smoothing: 0, deadbandDeg: 0, keepClipTilt: 0, turnTilt: 0, turnNod: 0, turnSettle: 0 }
const efCfg = { eyeWeight: 0, neckWeight: 0, maxYaw: 0, maxPitch: 0, yawRange: 0, pitchRange: 0, smoothing: 0, deadbandDeg: 0 }
const blinkCfg = { minInterval: 0, maxInterval: 0, closeMs: 0, holdMs: 0, openMs: 0 }
const weights = { head: 0, eye: 0, blink: 0 }
let lastStepAt = 0
let logOn = null

// ?looklog=1 — state changes and blinks in the console.
function maybeLog(out) {
  if (logOn === null) {
    try { logOn = new URLSearchParams(window.location.search).get('looklog') === '1' } catch { logOn = false }
  }
  if (!logOn) return
  if (out.transition) console.log(`[look] ${out.transition} → ${attentionStateLabel(st)}`)
  if (out.blink) console.log('[look] blink')
}

/** A setting's value: the wired input if there is one, else the node's field, else the default. */
function readSetting(field, data, readWired) {
  const wired = readWired ? readWired(field) : undefined
  if (wired !== undefined && wired !== null) {
    const n = Number(wired)
    return Number.isFinite(n) ? n : LOOK_DEFAULTS[field]
  }
  const v = data?.[field]
  if (typeof v === 'number' && Number.isFinite(v)) return v
  if (typeof v === 'boolean') return v ? 1 : 0
  const d = LOOK_DEFAULTS[field]
  return typeof d === 'boolean' ? (d ? 1 : 0) : d
}

export const lookRuntime = {
  /**
   * Advance one frame for a Look node.
   * @param {object} data      the node's data (its settings)
   * @param {(field:string) => number|undefined} [readWired]  value wired into an input, if any
   * @param {number} [weight]  the node's own enabled/weight (0 = off)
   * @returns {{head:number, eye:number, blink:number}} sentinel weights (reused object)
   */
  step(data, readWired, weight = 1) {
    const t = now()
    // No frame delta here; measure it, clamped after a stall (hidden tab).
    const dt = lastStepAt ? Math.min(0.1, Math.max(0, (t - lastStepAt) / 1000)) : 1 / 60
    lastStepAt = t

    for (const f of FIELDS) cfg[f] = readSetting(f, data, readWired)
    const w = weight > 0 ? (weight > 1 ? 1 : weight) : 0
    const active = w > 0 && cfg.active > 0.5
    const hold = cfg.holdEyeContact > 0.5
    const blinksOn = cfg.blinksOn > 0.5

    const out = stepAttention(st, cfg, lookTargetRuntime.scene, active, dt, Math.random, hold)
    if (out.blink && blinksOn) blinkNow()
    maybeLog(out)

    const p = lookTargetRuntime.provider
    p.stamp = t
    p.gate = out.gate
    p.away = out.away
    p.eyeAway = out.eyeAway
    p.headYaw = out.headYaw
    p.headPitch = out.headPitch
    p.eyeYaw = out.eyeYaw
    p.eyePitch = out.eyePitch
    p.headSmoothing = out.headSmoothing

    hfCfg.maxYaw = cfg.headMaxYaw
    hfCfg.maxPitch = cfg.headMaxPitch
    hfCfg.smoothing = cfg.headSpeed
    hfCfg.deadbandDeg = cfg.headDeadZone
    hfCfg.keepClipTilt = cfg.keepClipTilt
    hfCfg.turnTilt = cfg.turnTilt
    hfCfg.turnNod = cfg.turnNod
    hfCfg.turnSettle = cfg.turnSettle
    headFollowRuntime.setConfig(hfCfg)

    efCfg.eyeWeight = cfg.eyeAmount
    efCfg.neckWeight = 0 // the head has its own amount here — no hidden neck share
    efCfg.maxYaw = cfg.eyeMaxYaw
    efCfg.maxPitch = cfg.eyeMaxPitch
    efCfg.yawRange = cfg.eyeYawRange
    efCfg.pitchRange = cfg.eyePitchRange
    efCfg.smoothing = cfg.eyeSpeed
    efCfg.deadbandDeg = cfg.eyeDeadZone
    eyeFollowRuntime.setConfig(efCfg)

    blinkCfg.minInterval = cfg.blinkIntervalMin
    blinkCfg.maxInterval = cfg.blinkIntervalMax
    blinkCfg.closeMs = cfg.blinkCloseMs
    blinkCfg.holdMs = cfg.blinkHoldMs
    blinkCfg.openMs = cfg.blinkOpenMs
    setBlinkConfig(blinkCfg)

    const dbg = debugState.look
    dbg.state = attentionStateLabel(st)
    dbg.mode = st.mode
    dbg.holding = st.holding

    weights.head = cfg.headOn > 0.5 ? w * Math.max(0, Math.min(1, cfg.headAmount)) : 0
    weights.eye = cfg.eyesOn > 0.5 ? w : 0
    weights.blink = blinksOn ? w : 0
    return weights
  },

  // Debug handles: window.__LOOK__.state / .config / .stateLabel
  get state() { return st },
  get config() { return cfg },
  get stateLabel() { return attentionStateLabel(st) },
}

if (typeof window !== 'undefined') {
  window.__LOOK__ = lookRuntime
}
